import { AnalyticsEvents, ErrorOccurredProperties } from '@mondaygpt/analytics-events'
import { analytics } from 'services/mixpanel/analytics-config'

class ErrorTrackingService {
  private initialized = false

  initialize(): void {
    if (this.initialized) {
      console.warn('Error tracking already initialized')
      return
    }

    this.setupGlobalErrorHandlers()
    this.initialized = true
  }

  trackError(
    error: Error | string,
    context?: {
      error_code?: string
      stack_trace?: string
    }
  ): void {
    try {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const stackTrace = context?.stack_trace || (error instanceof Error ? error.stack : undefined)

      analytics.track(AnalyticsEvents.Errors.ERROR_OCCURRED, {
        error_message: errorMessage,
        error_code: context?.error_code,
        page: this.getCurrentPage(),
        stack_trace: stackTrace,
      } satisfies ErrorOccurredProperties)
    } catch (trackingError) {
      console.error('Failed to track error:', trackingError)
    }
  }

  /**
   * Setup global error event listeners
   */
  private setupGlobalErrorHandlers(): void {
    // Handle JavaScript runtime errors
    window.addEventListener('error', (event: ErrorEvent) => {
      this.trackError(event.error || event.message, {
        error_code: 'unhandled_javascript_error',
        stack_trace: event.error?.stack,
      })
    })

    // Handle unhandled Promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason
      const errorMessage = error instanceof Error ? error.message : String(error)

      this.trackError(errorMessage, {
        error_code: 'unhandled_promise_rejection',
        stack_trace: error instanceof Error ? error.stack : undefined,
      })
    })
  }

  private getCurrentPage(): string {
    try {
      return window.location.pathname + window.location.search
    } catch {
      return 'unknown'
    }
  }
}

export const errorTrackingService = new ErrorTrackingService()

export const initializeErrorTracking = () => {
  errorTrackingService.initialize()
}
